/**
 * Claude (또는 다른 LLM) 로 추출한 견적서 JSON 을 폼에 자동 채움.
 *
 * 흐름:
 *   1. 사용자가 매입처 견적서를 Claude.ai/Claude 앱에 올리고 본 모달의 안내문을 복사·전송
 *   2. Claude 가 ```json 코드 블록 하나로 응답 → 코드 상자의 [복사] 단추로 복사
 *   3. 사용자가 응답을 본 모달 textarea 에 붙여넣기 → 읽은 내용(품목·수량·단가·합계) 확인
 *   4. [폼에 채우기] → 발주서 작성 폼의 수신/참조/프로젝트명/품목 필드가 자동 채워짐
 *
 * 읽기·안내문은 lib/quote-import.ts (v2.213.1 — 앞뒤 설명 글·빠진 역슬래시·끝 쉼표·글자로 온 숫자까지 읽고,
 *   매입처 이름은 거래처 목록 이름으로 맞춘다). 전에는 흠 없는 JSON 만 읽어 「JSON 형식 오류」로 자주 멈췄다.
 *
 * Claude.ai Max 구독자라면 비용 0 (구독 한도 내 무료).
 */

import { useEffect, useMemo, useState } from 'react';
import { computePoTotals, type PoType } from '../../lib/po-api';
import {
  parseQuoteText,
  QUOTE_IMPORT_PROMPT,
  resolveSupplier,
  type ImportedQuote,
} from '../../lib/quote-import';

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (data: ImportedQuote) => void;
  /** 지금 폼의 값 — 「무엇이 바뀌나」 안내와 매입처 이름 맞추기용 */
  current: { recipientSupplier: string; projectName: string; poType: PoType; itemCount: number };
  /** 매입처 이름 후보 — 거래처 목록(매입처·양쪽)이 앞, 매입 이력이 뒤 (폼의 수신 자동완성과 같은 목록) */
  knownSuppliers: string[];
}

export function QuoteImportModal({ open, onClose, onApply, current, knownSuppliers }: Props) {
  const [jsonText, setJsonText] = useState('');
  const [promptCopied, setPromptCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setJsonText('');
    setPromptCopied(false);
  }, [open]);

  const result = useMemo(() => parseQuoteText(jsonText), [jsonText]);
  const quote = result?.ok ? result.quote : null;
  const supplier = useMemo(
    () => resolveSupplier(quote?.recipientSupplier ?? '', current.recipientSupplier, knownSuppliers),
    [quote, current.recipientSupplier, knownSuppliers],
  );
  const totals = useMemo(() => computePoTotals(quote?.items ?? []), [quote]);

  function handleCopyPrompt() {
    navigator.clipboard.writeText(QUOTE_IMPORT_PROMPT).then(
      () => {
        setPromptCopied(true);
        setTimeout(() => setPromptCopied(false), 2000);
      },
      () => alert('클립보드 복사 실패 — 직접 선택해서 복사해주세요'),
    );
  }

  if (!open) return null;

  const canApply =
    !!quote && (quote.items.length > 0 || !!supplier.name || !!quote.reference || !!quote.projectName);
  const curSupplier = current.recipientSupplier.trim();
  const curProject = current.projectName.trim();

  /** 품목별로 눈여겨볼 것 — 조용히 0·1 로 두지 않고 알린다 */
  const itemWarnings: string[] = [];
  quote?.items.forEach((it, idx) => {
    const no = `${idx + 1}번 품목`;
    if (it.qtyUnread) {
      itemWarnings.push(`${no}: 수량 「${it.qtyRaw || '빈 값'}」 — 숫자로 못 읽어 1 로 둡니다`);
    }
    if (it.priceUnread) {
      itemWarnings.push(`${no}: 단가 「${it.priceRaw || '빈 값'}」 — 숫자로 못 읽어 0원으로 둡니다`);
    } else if (it.unitPrice < 0) {
      itemWarnings.push(`${no}: 음수 단가는 저장되지 않습니다 — 할인은 단가에 반영해 주세요`);
    } else if (it.unitPrice === 0) {
      itemWarnings.push(`${no}: 단가가 0원입니다`);
    }
  });

  return (
    <div
      className="fixed inset-0 z-[65] bg-black/40 flex items-start justify-center overflow-y-auto p-4"
    >
      <div
        className="bg-white rounded shadow-xl w-full max-w-3xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="ui-modal-title">📋 매입처 견적서 자동 채움</h2>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
          >
            ×
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* 단계 1 — 안내문 복사 + Claude 작업 안내 */}
          <section>
            <h3 className="ui-subsection-title mb-2">
              1단계 — Claude 에서 견적서 추출
            </h3>
            <ol className="text-xs text-slate-600 list-decimal pl-5 space-y-0.5 mb-2">
              <li>아래 안내문을 [📋 복사]</li>
              <li>Claude(claude.ai·Claude 앱)에 <b>매입처 견적서 (PDF/이미지)</b>를 올리고, 안내문을 붙여 전송</li>
              <li>
                Claude 답의 <b>코드 상자 오른쪽 위 [복사]</b> 단추로 복사
                <span className="text-slate-400"> — 화면 글자를 마우스로 긁어 복사하면 따옴표(2.5&quot; 같은 인치 표시)가 빠질 수 있습니다</span>
              </li>
            </ol>
            <div className="relative">
              <textarea
                value={QUOTE_IMPORT_PROMPT}
                readOnly
                rows={8}
                className="ui-input ui-textarea w-full font-mono bg-slate-50 resize-none"
              />
              <button
                type="button"
                onClick={handleCopyPrompt}
                className="btn btn-primary btn-sm absolute top-2 right-2"
              >
                {promptCopied ? '✓ 복사됨' : '📋 복사'}
              </button>
            </div>
          </section>

          {/* 단계 2 — 답 붙여넣기 */}
          <section>
            <h3 className="ui-subsection-title mb-2">
              2단계 — Claude 답 붙여넣기
              <span className="ml-2 text-xs font-normal text-slate-500">앞뒤에 설명 글이 섞여 있어도 됩니다</span>
            </h3>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              placeholder={`예시:\n{\n  "recipientSupplier": "한결피아이에프",\n  "reference": "김근영 팀장님",\n  "items": [\n    { "itemName": "QNAP NAS", "description": "TS-873AeU-RP-4G", "qty": 1, "unitPrice": 3400000, "note": "자가설치" }\n  ]\n}`}
              rows={10}
              className="ui-input ui-textarea w-full font-mono resize-y"
            />
            {result && !result.ok && (
              <div className="mt-2 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-xs space-y-1">
                <div className="font-bold">
                  읽지 못했습니다 — {result.reason}
                  {result.line !== null && <span className="font-normal"> ({result.line}번째 줄 부근)</span>}
                </div>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li>
                    Claude 답의 <b>코드 상자 오른쪽 위 [복사]</b> 단추로 다시 복사해 붙여 주세요 — 화면 글자를 긁어 복사하면
                    따옴표가 빠질 수 있습니다.
                  </li>
                  <li>그래도 안 되면 Claude 에게 「JSON 코드 블록 하나로만 다시 줘」라고 보내 주세요.</li>
                </ul>
              </div>
            )}
          </section>

          {/* 추출 결과 미리보기 */}
          {result?.ok && quote && (
            <section>
              <h3 className="ui-subsection-title mb-2">3단계 — 읽은 내용 확인</h3>
              {(result.repaired || result.notes.length > 0) && (
                <div className="mb-2 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 rounded text-xs space-y-0.5">
                  {result.repaired && (
                    <div>
                      ⚠ 형식이 조금 틀린 곳(따옴표·쉼표·줄바꿈 등)을 고쳐 읽었습니다 — 아래 내용이 견적서와 같은지 꼭 확인하세요.
                    </div>
                  )}
                  {result.notes.map((n) => (
                    <div key={n}>⚠ {n}</div>
                  ))}
                </div>
              )}
              {result.outsideText && (
                <div className="mb-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
                  <span className="font-medium text-slate-700">JSON 밖에 적힌 말</span> (폼에 넣지 않음 — 참고만):{' '}
                  <span className="whitespace-pre-wrap">{result.outsideText}</span>
                </div>
              )}
              <div className="border border-emerald-200 bg-emerald-50/50 rounded p-3 text-xs space-y-1">
                <div>
                  <span className="font-medium text-slate-600">수신 (매입처):</span>{' '}
                  {supplier.name ? (
                    <>
                      <span className="text-slate-800">{supplier.name}</span>
                      {supplier.how === 'keep-current' && supplier.name !== quote.recipientSupplier && (
                        <span className="text-slate-500"> — 지금 폼과 같은 회사라 그대로 둡니다 (견적서 표기 「{quote.recipientSupplier}」)</span>
                      )}
                      {supplier.how === 'known' && supplier.name !== quote.recipientSupplier && (
                        <span className="text-slate-500"> — 견적서 표기 「{quote.recipientSupplier}」 → 거래처 목록 이름으로 맞췄습니다</span>
                      )}
                      {supplier.how === 'as-is' && !curSupplier && (
                        <span className="text-slate-400"> (거래처 목록에 없는 이름)</span>
                      )}
                      {supplier.how !== 'keep-current' && curSupplier && (
                        <div className="text-amber-700">
                          ↳ 지금 폼의 「{curSupplier}」에서 바뀝니다 — 결제조건도 이 매입처 기준으로 다시 채웁니다
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-400">(빈 값 — 채우지 않음)</span>
                  )}
                </div>
                <div>
                  <span className="font-medium text-slate-600">참조 (담당자):</span>{' '}
                  {quote.reference ? (
                    <span className="text-slate-800">{quote.reference}</span>
                  ) : (
                    <span className="text-slate-400">(빈 값 — 채우지 않음)</span>
                  )}
                </div>
                {quote.projectName && (
                  <div>
                    <span className="font-medium text-slate-600">프로젝트명:</span>{' '}
                    <span className="text-slate-800">{quote.projectName}</span>
                    {curProject && curProject !== quote.projectName && (
                      <span className="text-slate-500"> — 폼에 적힌 「{curProject}」 그대로 둡니다</span>
                    )}
                  </div>
                )}
                <div>
                  <span className="font-medium text-slate-600">품목:</span>{' '}
                  {quote.itemsMissing ? (
                    <span className="text-amber-700">품목(items)을 찾지 못했습니다 — 품목은 채우지 않습니다</span>
                  ) : quote.items.length === 0 ? (
                    <span className="text-slate-400">0줄 — 품목은 채우지 않습니다</span>
                  ) : (
                    <>
                      <span className="text-slate-800">{quote.items.length}줄</span>
                      {current.itemCount > 0 && (
                        <span className="text-slate-500"> — 지금 폼의 품목 {current.itemCount}줄을 이것으로 바꿉니다</span>
                      )}
                    </>
                  )}
                </div>
                {quote.items.length > 0 && current.poType === 'quote_attached' && (
                  <div className="text-amber-700">
                    ↳ 지금은 「② 견적서 첨부형」입니다 — 채우면 「① 품목 직접 입력」으로 바뀝니다
                  </div>
                )}
              </div>

              {quote.items.length > 0 && (
                <div className="mt-2 overflow-x-auto border border-slate-200 rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-1.5 py-1 w-6 text-center font-medium">#</th>
                        <th className="px-1.5 py-1 w-40 text-left font-medium">품목</th>
                        <th className="px-1.5 py-1 min-w-[10rem] text-left font-medium">상세 내용</th>
                        <th className="px-1.5 py-1 w-12 text-right font-medium">수량</th>
                        <th className="px-1.5 py-1 w-24 text-right font-medium">공급단가</th>
                        <th className="px-1.5 py-1 w-24 text-right font-medium">공급합계</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quote.items.map((it, idx) => {
                        const lines = it.description ? it.description.split('\n') : [];
                        return (
                          <tr key={idx} className="border-t border-slate-100 align-top">
                            <td className="px-1.5 py-1 text-center text-slate-500">{idx + 1}</td>
                            <td className="px-1.5 py-1 text-slate-800 break-words">{it.itemName || <span className="text-slate-300">—</span>}</td>
                            <td className="px-1.5 py-1 text-slate-600">
                              <span className="line-clamp-1 break-all" title={it.description}>{lines[0] ?? ''}</span>
                              {lines.length > 1 && <span className="text-slate-400"> 외 {lines.length - 1}줄</span>}
                            </td>
                            <td className={`px-1.5 py-1 text-right ${it.qtyUnread ? 'bg-amber-100 text-amber-800' : 'text-slate-800'}`}>
                              {it.qty.toLocaleString()}
                            </td>
                            <td
                              className={`px-1.5 py-1 text-right ${
                                it.priceUnread || it.unitPrice <= 0 ? 'bg-amber-100 text-amber-800' : 'text-slate-800'
                              }`}
                            >
                              {it.unitPrice.toLocaleString()}
                            </td>
                            <td className="px-1.5 py-1 text-right text-slate-800 font-medium">
                              {(totals.lineTotals[idx] ?? 0).toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t border-slate-200">
                      <tr>
                        <td colSpan={5} className="px-1.5 py-1 text-right text-slate-600">
                          공급가 합계 (VAT 별도) · <span className="text-slate-400">견적서 합계와 같은지 확인하세요</span>
                        </td>
                        <td className="px-1.5 py-1 text-right font-bold text-slate-800">{totals.totalSupply.toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td colSpan={5} className="px-1.5 py-1 text-right text-slate-600">VAT 포함</td>
                        <td className="px-1.5 py-1 text-right font-bold text-emerald-700">{totals.totalWithVat.toLocaleString()}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              {itemWarnings.length > 0 && (
                <ul className="mt-2 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 rounded text-xs space-y-0.5 list-none">
                  {itemWarnings.map((w) => (
                    <li key={w}>⚠ {w}</li>
                  ))}
                </ul>
              )}
              <div className="mt-2 text-xs text-slate-500">
                폼에 채운 뒤에도 고칠 수 있습니다. 잘못 읽혔으면 폼에서 직접 고치세요.
              </div>
            </section>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            취소
          </button>
          <button
            type="button"
            onClick={() => {
              if (!quote || !canApply) return;
              onApply({ ...quote, recipientSupplier: supplier.name });
              onClose();
            }}
            disabled={!canApply}
            className="btn btn-primary"
          >
            폼에 채우기
          </button>
        </div>
      </div>
    </div>
  );
}
